import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  invoiceService, 
  CreateInvoiceDto, 
  UpdateInvoiceDto,
  BulkUpdateStatusDto,
  InvoiceFilters
} from '../services';
import { Invoice } from '../types';
import { useSessionRole } from './useUsers';

/**
 * Query Keys
 * Centralized query key factory for invoices
 */
export const invoiceKeys = {
  all: ['invoices'] as const,
  lists: () => [...invoiceKeys.all, 'list'] as const,
  list: (filters?: InvoiceFilters, role?: string | null) =>
    [...invoiceKeys.lists(), filters, role ?? null] as const,
  details: () => [...invoiceKeys.all, 'detail'] as const,
  detail: (id: string) => [...invoiceKeys.details(), id] as const,
  auditLogs: (id: string) => [...invoiceKeys.detail(id), 'audit-logs'] as const,
  allStatistics: () => [...invoiceKeys.all, 'statistics'] as const,
  statistics: (month?: string, role?: string | null) =>
    [...invoiceKeys.allStatistics(), month, role ?? null] as const,
};

// Cache duration for invoice data. Kept short because the server scopes the list by the
// caller's role: a long-lived cache outlives a role change and shows a stale scope.
const INVOICES_CACHE_TTL_MS = 30 * 1000;

/**
 * Hook: useInvoices
 * Fetch invoices with filters and pagination.
 *
 * The query is keyed by the session role as well as the filters, so a role change can never
 * serve rows fetched under the previous role.
 */
export const useInvoices = (filters?: InvoiceFilters) => {
  const { role } = useSessionRole();

  return useQuery({
    queryKey: invoiceKeys.list(filters, role),
    queryFn: () => invoiceService.getAll(filters),
    staleTime: INVOICES_CACHE_TTL_MS,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: true,
  });
};

/**
 * Hook: useInvoice
 * Fetch a single invoice by ID
 */
export const useInvoice = (id: string) => {
  return useQuery({
    queryKey: invoiceKeys.detail(id),
    queryFn: () => invoiceService.getById(id),
    enabled: !!id, // Only run if ID exists
  });
};

/**
 * Hook: useInvoiceAuditLogs
 * Fetch audit logs for an invoice
 */
export const useInvoiceAuditLogs = (id: string) => {
  return useQuery({
    queryKey: invoiceKeys.auditLogs(id),
    queryFn: () => invoiceService.getAuditLogs(id),
    enabled: !!id,
  });
};

/**
 * Hook: useInvoiceStatistics
 * Fetch invoice statistics
 */
export const useInvoiceStatistics = (month?: string) => {
  const { role } = useSessionRole();

  return useQuery({
    queryKey: invoiceKeys.statistics(month, role),
    queryFn: () => invoiceService.getStatistics(month),
  });
};

/**
 * Hook: useCreateInvoice
 * Create a new invoice
 */
export const useCreateInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, file }: { data: CreateInvoiceDto; file: File }) =>
      invoiceService.create(data, file),
    onSuccess: () => {
      // Invalidate and refetch invoice lists
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.allStatistics() });
    },
  });
};

/**
 * Hook: useUpdateInvoice
 * Update an invoice
 */
export const useUpdateInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateInvoiceDto }) =>
      invoiceService.update(id, data),
    onSuccess: (updatedInvoice) => {
      // Update the specific invoice in cache
      queryClient.setQueryData(
        invoiceKeys.detail(updatedInvoice.id),
        updatedInvoice
      );
      
      // Invalidate lists to refetch
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.allStatistics() });
    },
  });
};

/**
 * Hook: useDeleteInvoice
 * Delete an invoice
 */
export const useDeleteInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => invoiceService.delete(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: invoiceKeys.detail(deletedId) });
      
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.allStatistics() });
    },
  });
};

/**
 * Hook: useBulkUpdateInvoiceStatus
 * Bulk update invoice status
 */
export const useBulkUpdateInvoiceStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulkUpdateStatusDto) => invoiceService.bulkUpdateStatus(data),
    onSuccess: () => {
      // Invalidate all invoice queries
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
    },
  });
};
